import React, { useState, useEffect } from 'react';
import { authService } from '../services/auth.service';
import { Trash2, Edit2, Plus } from 'lucide-react';

interface Species {
  id: number;
  name: string;
  _count: {
    posts: number;
    breeds: number;
    sizes: number;
  };
}

export default function SpeciesManagement() {
  const [species, setSpecies] = useState<Species[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSpecies, setEditingSpecies] = useState<Species | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    loadSpecies();
  }, []);

  const loadSpecies = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await authService.makeAuthenticatedRequest('/animal-species/species');
      if (response.ok) {
        const data = await response.json();
        setSpecies(Array.isArray(data) ? data : []);
      } else {
        setError('Erro ao carregar espécies');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar espécies');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    setSaveLoading(true);
    setError('');

    try {
      const sanitizedName = name.trim();

      if (editingSpecies) {
        const response = await authService.makeAuthenticatedRequest(
          `/animal-species/species/${editingSpecies.id}`,
          {
            method: 'PUT',
            body: JSON.stringify({ name: sanitizedName }),
          }
        );

        if (response.ok) {
          await loadSpecies();
          setSuccess('Espécie atualizada com sucesso!');
          closeModal();
          setTimeout(() => setSuccess(''), 5000);
        } else {
          const errorData = await response.json();
          setError(errorData.message || 'Erro ao atualizar espécie');
        }
      } else {
        const response = await authService.makeAuthenticatedRequest(
          '/animal-species/species',
          {
            method: 'POST',
            body: JSON.stringify({ name: sanitizedName }),
          }
        );

        if (response.ok) {
          await loadSpecies();
          setSuccess('Espécie criada com sucesso!');
          closeModal();
          setTimeout(() => setSuccess(''), 5000);
        } else {
          const errorData = await response.json();
          setError(errorData.message || 'Erro ao criar espécie');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar espécie');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (id: number, speciesName: string) => {
    if (!window.confirm(`Tem certeza que deseja excluir a espécie "${speciesName}"?`)) {
      return;
    }

    try {
      const response = await authService.makeAuthenticatedRequest(
        `/animal-species/species/${id}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        await loadSpecies();
        setSuccess('Espécie excluída com sucesso!');
        setTimeout(() => setSuccess(''), 5000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Erro ao excluir espécie');
        setTimeout(() => setError(''), 8000);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao excluir espécie');
      setTimeout(() => setError(''), 8000);
    }
  };

  const openModalToAdd = () => {
    setEditingSpecies(null);
    setName('');
    setError('');
    setShowModal(true);
  };

  const openModalToEdit = (sp: Species) => {
    setEditingSpecies(sp);
    setName(sp.name);
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSpecies(null);
    setName('');
    setError('');
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: 'var(--card-foreground)' }}>
          Espécies Cadastradas
        </h2>
        <button
          onClick={openModalToAdd}
          style={{
            background: 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
            color: 'white',
            fontWeight: 600,
            padding: '12px 20px',
            border: 'none',
            borderRadius: 10,
            fontSize: 15,
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
          }}
        >
          <Plus size={18} />
          Nova Espécie
        </button>
      </div>

      {success && (
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          padding: 16,
          borderRadius: 8,
          marginBottom: 24,
          fontWeight: 500,
          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
        }}>
          {success}
        </div>
      )}

      {error && (
        <div style={{
          background: '#ef4444',
          color: 'white',
          padding: 16,
          borderRadius: 8,
          marginBottom: 24,
          fontWeight: 500,
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted-foreground)' }}>
          Carregando espécies...
        </div>
      ) : (
        <div style={{ background: 'var(--card)', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--muted)', color: 'var(--card-foreground)' }}>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600 }}>Nome</th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: 600 }}>Posts</th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: 600 }}>Raças</th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: 600 }}>Tamanhos</th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: 600 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {species.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--muted-foreground)' }}>
                    Nenhuma espécie cadastrada
                  </td>
                </tr>
              ) : (
                species.map((sp, index) => (
                  <tr
                    key={sp.id}
                    style={{
                      background: index % 2 === 0 ? 'var(--card)' : 'var(--muted)',
                      borderBottom: '1px solid var(--border)',
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--accent)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = index % 2 === 0 ? 'var(--card)' : 'var(--muted)')}
                  >
                    <td style={{ padding: '16px', fontWeight: 600 }}>{sp.name}</td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>{sp._count?.posts || 0}</td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>{sp._count?.breeds || 0}</td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>{sp._count?.sizes || 0}</td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                        <button
                          onClick={() => openModalToEdit(sp)}
                          style={{
                            background: 'var(--primary)',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 14,
                            fontWeight: 600,
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          <Edit2 size={14} />
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(sp.id, sp.name)}
                          style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 14,
                            fontWeight: 600,
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          <Trash2 size={14} />
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeModal}
        >
          <div
            style={{
              background: 'var(--card)',
              padding: 32,
              borderRadius: 16,
              width: '100%',
              maxWidth: 500,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20, color: 'var(--card-foreground)' }}>
              {editingSpecies ? 'Editar Espécie' : 'Nova Espécie'}
            </h2>
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label htmlFor="name" style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: 'var(--card-foreground)' }}>
                  Nome da Espécie
                </label>
                <input
                  id="name"
                  type="text"
                  placeholder="Ex: Cachorro"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{
                    width: '100%',
                    background: 'var(--input)',
                    border: '2px solid var(--border)',
                    borderRadius: 8,
                    padding: 12,
                    fontSize: 16,
                    boxSizing: 'border-box',
                  }}
                  required
                />
              </div>
              {error && <div style={{ color: '#ef4444', fontSize: 14 }}>{error}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
                <button
                  type="button"
                  onClick={closeModal}
                  style={{
                    background: 'var(--muted)',
                    color: 'var(--card-foreground)',
                    padding: '10px 20px',
                    borderRadius: 8,
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saveLoading}
                  style={{
                    background: saveLoading ? 'var(--muted)' : 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
                    color: 'white',
                    fontWeight: 600,
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: 8,
                    cursor: saveLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {saveLoading ? 'Salvando...' : editingSpecies ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
